// CoverImage — resilient blog cover image.
// Local /images/blog covers are inconsistent: the data references .png but most
// files on disk are actually .webp, while a handful of recent articles really
// are .png. So try .webp first (the common case), fall back to the original
// extension, then a category-tinted placeholder. Shared by the blog index and
// category pages.

import { useState } from "react";
import { getCategoryBySlug } from "@/lib/blogMeta.generated";

const ff = "'Barlow', 'Barlow Fallback', sans-serif";

function candidates(src: string): string[] {
  if (src.startsWith("/images/blog/")) {
    if (src.endsWith(".png")) return [src.slice(0, -4) + ".webp", src];
    if (src.endsWith(".webp")) return [src, src.slice(0, -5) + ".png"];
  }
  return [src];
}

export default function CoverImage({ post, imgStyle, eager }: {
  post: { coverImage: string; title: string; categories: string[] };
  imgStyle: React.CSSProperties;
  eager?: boolean;
}) {
  // NB: a generated-cover variant of this component was tried and reverted.
  // Covers are the shipped image files, as they always were.
  const srcs = candidates(post.coverImage);
  const [idx, setIdx] = useState(0);
  const cat = getCategoryBySlug(post.categories[0]);
  const color = cat?.color ?? "#57d7e2";

  // Exhausted every candidate — show a category-tinted placeholder.
  if (idx >= srcs.length) {
    return (
      <div aria-hidden="true" style={{
        width: "100%",
        height: "100%",
        background: `${color}14`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <span style={{
          color,
          opacity: 0.55,
          fontFamily: ff,
          fontSize: "12px",
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase" as const,
        }}>{cat?.label ?? "Indexed"}</span>
      </div>
    );
  }

  return (
    <img
      key={srcs[idx]}
      src={srcs[idx]}
      alt={post.title}
      onError={() => setIdx((i) => i + 1)}
      loading={eager ? undefined : "lazy"}
      fetchPriority={eager ? "high" : undefined}
      decoding="async"
      style={imgStyle}
    />
  );
}
